import { useCreateSkillAction } from "@/action/use-create-skill-action";
import {
  EXPERT_ICON,
  SKILL_ICON,
  TEMPLATE_ICON,
} from "@/components/display/display-icon/artifacts";
import { LinkDriveFolderDialog } from "@/components/folder/LinkDriveFolderDialog";
import { BulkCreateSkillButton } from "@/components/skill";
import { Button } from "@/components/ui/button";
import { useExpertEditData } from "@/data/use-expert-edit-data";
import { useExpertSkillsData } from "@/data/use-expert-skills-data";
import { useFoldersData } from "@/data/use-folders-data";
import { useGroupSkillsData } from "@/data/use-group-skills-data";
import { usePerspectivesData } from "@/data/use-perspectives-data";
import { useGroupTemplatesData } from "@/data/use-templates-data";
import { grantControllerSetPublished } from "@/lib/gen-api";
import { queryKeys } from "@/lib/query-keys";
import { Template } from "@/types/types";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faAdd,
  faBooks,
  faFileImport,
  faGlasses,
} from "@nnr/fontawesome-sharp-light/icons/sharp/light";
import { useQueryClient } from "@tanstack/react-query";
import { useMatchRoute, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { toast } from "sonner";
import { KitMenuTemplate, KitMenuTemplateProps } from "../KitMenuTemplate";
import {
  SETTINGS_GROUP_PARTIAL,
  useDocumentGroup,
  useExpertKnowledgeGroup,
  useFolderGroup,
} from "./hooks";
import { KitTypeMenuProps } from "./types";

export const ExpertCreatorMenu: React.FC<KitTypeMenuProps> = ({
  mode,
  groupUuid,
  groupSlug,
  kit,
}) => {
  const navigate = useNavigate();
  const matchRoute = useMatchRoute();
  const queryClient = useQueryClient();
  const [driveFolderDialog, setDriveFolderDialog] = useState(false);
  const [isBulkImportOpen, setIsBulkImportOpen] = useState(false);
  const documentGroup = useDocumentGroup({
    groupUuid,
    groupSlug,
    kitUuid: kit.uuid,
  });
  const { expertUuid } = useExpertEditData({
    groupUuid,
    kitUuid: kit.uuid,
  });
  const {
    folders: expertFolders,
    associateFolder,
    dissociateFolder,
  } = useFoldersData({ expertUuid });
  const expertFolderUuids = expertFolders.map((folder) => folder.uuid);
  const expertKnowledgeGroup = useExpertKnowledgeGroup({
    groupUuid,
    kitUuid: kit.uuid,
    expertFolderUuids,
    onAssociate: (folderUuid) => associateFolder({ folderUuid }),
    onDissociate: (folderUuid) => dissociateFolder({ folderUuid }),
  });
  const folderGroup = useFolderGroup({
    groupUuid,
    kitUuid: kit.uuid,
    onLinkDriveFolderClick: () => setDriveFolderDialog(true),
    excludeFolderUuids: expertFolderUuids,
    itemDropdownMenu: (folderUuid) => [
      <Button
        variant="ghost"
        size="icon-sm"
        onClick={() => associateFolder({ folderUuid })}
      >
        <FontAwesomeIcon icon={faBooks} />
        Set as Expert Knowledge
      </Button>,
    ],
  });
  const { perspectives, createPerspective } = usePerspectivesData(
    groupUuid,
    kit.uuid,
    expertUuid,
  );
  const { templates } = useGroupTemplatesData({
    groupUuid,
  });
  const { create: createSkill } = useCreateSkillAction();

  // Skills
  const { skills: groupSkills } = useGroupSkillsData({
    groupUuid,
  });
  const { skillUuids, associateSkill, isAssociating } = useExpertSkillsData(
    groupUuid,
    kit.uuid,
    expertUuid,
  );
  const linkedSkills = groupSkills.filter((skill) =>
    skillUuids.includes(skill.uuid),
  );
  const remainingGroupSkills = groupSkills.filter(
    (skill) => !skillUuids.includes(skill.uuid),
  );

  const perspectiveItems: KitMenuTemplateProps["groups"][number]["items"] =
    perspectives.map((perspective) => ({
      id: perspective.uuid,
      title: perspective.title,
      link: {
        to: "/w/$groupSlug/k/$kitUuid/perspectives/$perspectiveUuid" as const,
        params: {
          groupSlug,
          kitUuid: kit.uuid,
          perspectiveUuid: perspective.uuid,
        },
      },
      active: !!matchRoute({
        to: "/w/$groupSlug/k/$kitUuid/perspectives/$perspectiveUuid",
        params: {
          groupSlug,
          kitUuid: kit.uuid,
          perspectiveUuid: perspective.uuid,
        },
      }),
    }));

  const templateItems = templates.filter((template) =>
    perspectives.every(
      (perspective) => perspective.template_uuid !== template.uuid,
    ),
  );

  const handleCreateSkill = async () => {
    const { skillUuid } = await createSkill({
      createGroupAcl: true,
      category: "General",
    });
    await grantControllerSetPublished({
      body: {
        isPublished: true,
        skillUuid,
      },
    });
    queryClient.invalidateQueries({
      queryKey: queryKeys.groupSkills({
        groupUuid: groupUuid!,
      }),
    });
    await associateSkill({ skillUuid });
    navigate({
      to: "/w/$groupSlug/k/$kitUuid/skills/$skillUuid",
      params: { groupSlug, kitUuid: kit.uuid, skillUuid },
    });
  };

  const handleCreatePerspective = async (template: Template) => {
    const perspective = await createPerspective({
      title: `${template.name} Perspective`,
      template_uuid: template.uuid,
      instructions: "",
    });
    if (perspective) {
      navigate({
        to: "/w/$groupSlug/k/$kitUuid/perspectives/$perspectiveUuid",
        params: {
          groupSlug,
          kitUuid: kit.uuid,
          perspectiveUuid: perspective.uuid,
        },
      });
    } else {
      toast.error("Failed to create perspective. Please try again.");
    }
  };

  const groups: KitMenuTemplateProps["groups"] = [
    {
      title: "Persona",
      icon: EXPERT_ICON,
      active:
        !!matchRoute({
          to: "/w/$groupSlug/k/$kitUuid/expert",
          params: { groupSlug, kitUuid: kit.uuid },
          fuzzy: true,
        }) &&
        !matchRoute({
          to: "/w/$groupSlug/k/$kitUuid/expert-settings",
          params: { groupSlug, kitUuid: kit.uuid },
        }),
      items: [
        {
          id: "context",
          title: "Context",
          link: {
            to: "/w/$groupSlug/k/$kitUuid/expert/context" as const,
            params: { groupSlug, kitUuid: kit.uuid },
          },
          active: !!matchRoute({
            to: "/w/$groupSlug/k/$kitUuid/expert/context",
            params: { groupSlug, kitUuid: kit.uuid },
          }),
        },
        {
          id: "tone-of-voice",
          title: "Tone of Voice",
          link: {
            to: "/w/$groupSlug/k/$kitUuid/expert/tone-of-voice" as const,
            params: { groupSlug, kitUuid: kit.uuid },
          },
          active: !!matchRoute({
            to: "/w/$groupSlug/k/$kitUuid/expert/tone-of-voice",
            params: { groupSlug, kitUuid: kit.uuid },
          }),
        },
      ],
    },
    {
      title: "Skills",
      icon: SKILL_ICON,
      showCounter: true,
      items: linkedSkills.map((skill) => ({
        id: skill.uuid,
        title: skill.name,
        link: {
          to: "/w/$groupSlug/k/$kitUuid/skills/$skillUuid" as const,
          params: { groupSlug, kitUuid: kit.uuid, skillUuid: skill.uuid },
        },
        active: !!matchRoute({
          to: "/w/$groupSlug/k/$kitUuid/skills/$skillUuid",
          params: { groupSlug, kitUuid: kit.uuid, skillUuid: skill.uuid },
        }),
      })),
      menu: [
        {
          title: "Add Skill",
          icon: faAdd,
          items: [
            {
              id: "create-skill",
              title: "Create Skill",
              icon: faAdd,
              onClick: handleCreateSkill,
            },
            {
              id: "bulk-import-skills",
              title: "Import Skills",
              icon: faFileImport,
              onClick: () => setIsBulkImportOpen(true),
            },
            ...remainingGroupSkills.map((skill) => ({
              id: skill.uuid,
              title: skill.name,
              icon: SKILL_ICON,
              disabled: isAssociating || skillUuids.includes(skill.uuid),
              onClick: async () => {
                if (skillUuids.includes(skill.uuid)) return;
                await associateSkill({ skillUuid: skill.uuid });
                navigate({
                  to: "/w/$groupSlug/k/$kitUuid/skills/$skillUuid",
                  params: {
                    groupSlug,
                    kitUuid: kit.uuid,
                    skillUuid: skill.uuid,
                  },
                });
              },
            })),
          ],
        },
      ],
    },
    folderGroup,
    expertKnowledgeGroup,
    {
      title: "Perspectives",
      icon: faGlasses,
      items: perspectiveItems,
      showCounter: true,
      menu: [
        {
          title: "New Perspective",
          icon: faAdd,
          items: templateItems.map((template) => ({
            id: template.uuid,
            title: template.name,
            icon: TEMPLATE_ICON,
            onClick: () => handleCreatePerspective(template),
          })),
        },
      ],
    },
    {
      ...documentGroup,
      title: "Playground",
    },
    {
      ...SETTINGS_GROUP_PARTIAL,
      active: !!matchRoute({
        to: "/w/$groupSlug/k/$kitUuid/expert-settings",
        params: { groupSlug, kitUuid: kit.uuid },
        fuzzy: true,
      }),
      link: {
        to: "/w/$groupSlug/k/$kitUuid/expert-settings" as const,
        params: { groupSlug, kitUuid: kit.uuid },
      },
    },
  ];
  return (
    <>
      <KitMenuTemplate kit={kit} groups={groups} mode={mode} />
      <BulkCreateSkillButton
        open={isBulkImportOpen}
        onOpenChange={setIsBulkImportOpen}
        expertUuid={expertUuid ?? undefined}
        kitUuid={kit.uuid}
      />
      <LinkDriveFolderDialog
        open={driveFolderDialog}
        onOpenChange={setDriveFolderDialog}
        groupUuid={groupUuid}
        groupSlug={groupSlug}
        kitUuid={kit.uuid}
      />
    </>
  );
};
